import { createElement } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { AppHost } from './host.js';

/**
 * The Business OS, kept out of the customer's first load and off the customer's host.
 *
 * ── THE MEASUREMENT THAT PROMPTED THIS ──────────────────────────────────────────
 * One chunk, 570.41 kB raw / 149.89 kB gzip, and `vite build` warning about it on every
 * run. Inside it were ফাউন্ডার mode, the vendor console, the ops queue, HR, finance, the
 * cap table, the project board and the legal vault — every screen a CUSTOMER never opens.
 * A person on 2G in Mirpur was downloading the company's back office to look for an
 * electrician.
 *
 * ── WHY A PATH LIST RATHER THAN A LAZY IMPORT OF THE WHOLE GROUP ────────────────
 * React Router has to know a route's PATH before it can decide to load it; that is the
 * whole mechanism. `RouteObject.lazy` defers everything except the path, which is
 * exactly the right seam — but it means the paths have to exist on the eager side.
 *
 * ── WHERE THE PATH LIST LIVES NOW: routes/<group>/paths.ts ──────────────────────
 * This file used to hold every group's path list itself, which made it the one file
 * every Business OS package had to edit — and with several packages adding screens in
 * parallel, a shared list is a guaranteed merge conflict. Each deferred group now
 * declares itself in its own `paths.ts`: which host serves it, the paths its `routes.tsx`
 * mounts, and how to load that `routes.tsx`. This file finds them with
 * `import.meta.glob`, so a new group is a new directory, and neither this file nor
 * `App.tsx` changes. (`App.tsx` keeps one exclusion list, for a reason given there, and a
 * test fails if a group here is missing from it.)
 *
 * `paths.ts` is eager and tiny — strings and one `import()` — and `routes.tsx` stays
 * behind that `import()`, which is what keeps the group off the first load.
 *
 * Duplicating a path list is normally how two files drift apart in silence, and the
 * failure here is quiet and bad: a new /vendor screen lands, nobody adds it to
 * `paths.ts`, and the route falls through to the not-found screen. So the list is not
 * trusted — `__tests__/app-shell.test.ts` loads every group and asserts SET EQUALITY both
 * ways. A path added to a group and not declared fails; a declared path the group has
 * dropped fails too.
 *
 * ── WHAT IS AND IS NOT "BUSINESS OS" ────────────────────────────────────────────
 * The test is whether a customer can reach it from the drawn tab bar or any screen it
 * leads to. `/policy` stays eager: refund and cancellation terms are read by a customer
 * mid-checkout, and a page about getting money back must not itself need a download.
 * `/provider/:id/verification` stays eager for the same reason — it is the panel a
 * customer opens from an offering to see what যাচাইকৃত actually covers.
 *
 * ── WHICH HOST ──────────────────────────────────────────────────────────────────
 * Every deferred group is served on founder.oprappo.com EXCEPT the vendor console, which
 * stays on the customer host (founder decision 2026-09-18): a vendor is a member of the
 * public who signed up to sell, not somebody inside the company, and sending them to the
 * founder door would put them in front of a login that is not theirs. `host.ts` decides
 * which host a request is on; `App.tsx` decides what that host mounts.
 */

/** The door a deferred group is served behind. See `host.ts`. */
export type GroupHost = 'founder' | 'customer';

export interface DeferredGroup {
  /** Which hostname serves this group. `founder` groups are redirected from a customer host. */
  readonly host: GroupHost;
  /** Every path this group's own `routes.tsx` mounts. Asserted against it by a test. */
  readonly paths: readonly string[];
  /** Loaded on first navigation into the group, never before. */
  readonly load: () => Promise<readonly RouteObject[]>;
}

/**
 * Every `routes/<group>/paths.ts`, eagerly. Each exports `group: DeferredGroup`.
 *
 * Eager is correct here and only here: these modules are a few strings and an `import()`
 * each, and the router cannot match a path it has not been told about.
 */
const DECLARATIONS = import.meta.glob<{ group?: DeferredGroup }>('./routes/*/paths.ts', {
  eager: true,
});

/** `./routes/ops-money/paths.ts` → `ops-money`. */
export function groupName(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 2] ?? key;
}

/**
 * The deferred groups by directory name, in directory-name order.
 *
 * Sorted explicitly rather than trusting the glob's key order: route ORDER does not
 * decide a match in React Router 6 (it ranks by specificity), but a stable order keeps
 * the route table — and every diff of it — the same from one build to the next.
 *
 * A `paths.ts` that exports no `group` is left out rather than thrown on. A throw at
 * module scope would take the whole app down for every customer over one malformed file
 * in the back office; the app-shell test fails on it instead, where it costs nothing.
 */
export const DEFERRED_GROUPS: Readonly<Record<string, DeferredGroup>> = Object.fromEntries(
  Object.keys(DECLARATIONS)
    .sort()
    .flatMap((key) => {
      const group = DECLARATIONS[key]?.group;
      return group === undefined ? [] : [[groupName(key), group] as const];
    }),
);

/**
 * The groups whose screens read `/v1/founder/*`, and so need a founder-mode session.
 *
 * Their elements are wrapped in `routes/founder/gate.tsx § FounderGate` as they load:
 * signed out goes to sign-in, no founder session goes to FM1 with `?next=`, and a held
 * session gets the screen with its clock and the way out. Listed by NAME because the
 * reason is about each group's reads, not its host — the operations console is on the
 * founder host too, and an operator is not a founder and has no founder session to hold.
 * A group missing from this list still refuses on the server; it just refuses as
 * অনুমতি নেই instead of opening the door.
 */
export const FOUNDER_SESSION_GROUPS: readonly string[] = [
  'corporate',
  'finance',
  'founder',
  'hr',
  'legal',
  'projects',
];

/** The groups a given host mounts. `both` mounts every one. */
export function groupsServedOn(host: AppHost): Array<[string, DeferredGroup]> {
  return Object.entries(DEFERRED_GROUPS).filter(
    ([, group]) => host === 'both' || group.host === host,
  );
}

/**
 * Paths a CUSTOMER host must hand to the founder host rather than serve.
 *
 * Only a customer host sends anything anywhere. The founder host serves its own groups,
 * and `both` serves everything — see `host.ts § the third answer` for why that is what
 * stops localhost from redirecting to itself.
 */
export function founderPathsFor(host: AppHost): string[] {
  if (host !== 'customer') return [];
  return Object.values(DEFERRED_GROUPS)
    .filter((group) => group.host === 'founder')
    .flatMap((group) => [...group.paths]);
}

/**
 * One eager `RouteObject` per deferred path this host serves: the path is known now, the
 * screen arrives when somebody navigates to it.
 *
 * The group module is fetched once and then held by the module cache, so the second
 * screen in a group costs nothing — which matters, because nobody opens ফাউন্ডার mode to
 * look at one screen.
 *
 * A chunk that fails to load — the usual cause is a deploy that replaced the hashed file
 * this tab was about to ask for — rejects `lazy`, and the rejection reaches the root
 * error element in `App.tsx`, which reloads once onto the new build.
 */
export function deferredRoutes(host: AppHost = 'both'): RouteObject[] {
  return groupsServedOn(host).flatMap(([name, group]) =>
    group.paths.map((path): RouteObject => ({
      path,
      lazy: async () => {
        const routes = await group.load();
        const match = routes.find((route) => route.path === path);
        if (match === undefined || match.element === undefined) {
          /*
           * Not a silent fallback. If this throws, `paths.ts` and the group's own
           * routes.tsx have disagreed in a build that shipped — which the test is there
           * to prevent, so reaching here means the test was removed or skipped. An error
           * boundary showing a broken route is recoverable; a route that silently
           * renders nothing is the failure this whole file is written against.
           */
          throw new Error(
            `business-os: group "${name}" declares ${path} but its routes.tsx does not mount it`,
          );
        }
        if (!FOUNDER_SESSION_GROUPS.includes(name)) return { element: match.element };
        /*
         * THE GATE LOADS WITH THE GROUP, never before: it is behind the same `import()`
         * boundary, so the customer's first load carries none of it. FM1 is the one path
         * it lets through without a founder session — it is the door.
         */
        const { FounderGate } = await import('./routes/founder/gate.js');
        return {
          element: createElement(FounderGate, {
            host,
            entry: path === '/founder/enter',
            children: match.element,
          }),
        };
      },
    })),
  );
}
